import type { ReactNode } from 'react';
import { useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import type { FlexProps } from '@chakra-ui/react';
import { Box, Divider, Flex, Stack } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';

import { isEmptyArray } from '@izimi/utils';

import CustomIcon from '../icon/CustomIcon';
import MotionBox from '../MotionBox/MotionBox';
import { isReactNode } from '../util/react';
import NavigationHelpMenu from './NavigationHelpMenu';
import NavigationItem from './NavigationItem';
import NavigationMenuToggle from './NavigationMenuToggle';
import NavigationUserMenu from './NavigationUserMenu';
import type { NavigationMenuItem } from './types';

interface Props<Routes extends string> {
  renderLogo: (onClick: VoidFunction) => ReactNode;
  renderQuickActions?: ReactNode;
  renderUserMenuHeader?: ReactNode;
  mainMenu: NavigationMenuItem<Routes>[];
  userMenu: (NavigationMenuItem<Routes> | ReactNode)[];
  helpMenu: NavigationMenuItem<Routes>[];
  children?: ReactNode;
  userName: string;
  containerProps?: FlexProps;
}

const Navigation = <Routes extends string>({
  renderLogo,
  renderQuickActions,
  renderUserMenuHeader,
  mainMenu,
  userMenu,
  helpMenu,
  children,
  userName,
  containerProps,
}: Props<Routes>) => {
  const [isOpen, setIsOpen] = useState(false);

  const closeMobileMenu = () => setIsOpen(false);

  return (
    <Flex
      as='nav'
      direction={{ base: 'row', md: 'column' }}
      bg='neutral100'
      height={{ base: '70px', md: '100%' }}
      minHeight={{ base: '70px', md: '100%' }}
      minW={{ md: '100px' }}
      maxW={{ md: '100px' }}
      py={{ base: 4, md: 6 }}
      px={{ base: 5, md: 0 }}
      paddingInlineEnd={{ base: 2 }}
      position='sticky'
      top={0}
      zIndex='sticky'
      justifyContent='space-between'
      alignItems={{ base: 'center', md: 'revert' }}
      overflowY={{ base: 'hidden', md: 'auto' }}
      {...containerProps}>
      <Flex direction={{ base: 'row', md: 'column' }} alignItems='center'>
        {renderLogo(closeMobileMenu)}
        {renderQuickActions && (
          <Box mt={8} display={{ base: 'none', md: 'flex' }}>
            {renderQuickActions}
          </Box>
        )}
        <Stack width='100%' display={{ base: 'none', md: 'flex' }} mt={8}>
          {mainMenu.map((item) => (
            <NavigationItem
              data-testid={`main-menu-${item.label}`}
              key={item.label}
              to={item.href}
              label={item.label}
              icon={<CustomIcon name={item.customIconName} />}
              badge={item.badge}
              onMouseEnter={item.onHover}
              onFocus={item.onFocus}
            />
          ))}
        </Stack>
      </Flex>
      <Stack direction={{ base: 'row', md: 'column' }} spacing={{ base: 0, md: 6 }}>
        {!isEmptyArray(helpMenu) && <NavigationHelpMenu items={helpMenu} />}
        {children}
        <NavigationUserMenu
          userName={userName}
          renderUserMenuHeader={renderUserMenuHeader}
          items={userMenu}
        />
        <NavigationMenuToggle
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          data-testid='mobile-menu-toggle'
        />
        <AnimatePresence>
          {isOpen && (
            <RemoveScroll>
              <MotionBox
                w='100vw'
                h='calc(100% - 70px)'
                zIndex={10}
                position='fixed'
                top={70}
                left={0}
                bg='white'
                overflowY='auto'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}>
                <Stack spacing={6} py={6} px={5}>
                  {mainMenu.map((item) => (
                    <NavigationItem
                      data-testid={`mobile-menu-${item.href}`}
                      key={item.label}
                      type='mobile'
                      to={item.href}
                      label={item.label}
                      icon={<CustomIcon name={item.customIconName} />}
                      onClick={() => {
                        item.onClick?.();
                        closeMobileMenu();
                      }}
                      onMouseEnter={item.onHover}
                      onFocus={item.onFocus}
                    />
                  ))}
                  {!isEmptyArray(helpMenu) && (
                    <>
                      <Divider />
                      {helpMenu.map((item) => (
                        <NavigationItem
                          key={item.label}
                          type='mobile'
                          to={item.href}
                          label={item.label}
                          icon={<CustomIcon name={item.customIconName} />}
                          onClick={() => {
                            item.onClick?.();
                            closeMobileMenu();
                          }}
                          onMouseEnter={item.onHover}
                          onFocus={item.onFocus}
                        />
                      ))}
                    </>
                  )}
                  <Divider />
                  {userMenu.map((item) =>
                    isReactNode(item) ? (
                      item
                    ) : (
                      <NavigationItem
                        key={item.label}
                        type='mobile'
                        to={item.href}
                        label={item.label}
                        icon={<CustomIcon name={item.customIconName} />}
                        onClick={() => {
                          item.onClick?.();
                          closeMobileMenu();
                        }}
                        onMouseEnter={item.onHover}
                        onFocus={item.onFocus}
                      />
                    ),
                  )}
                </Stack>
              </MotionBox>
            </RemoveScroll>
          )}
        </AnimatePresence>
      </Stack>
    </Flex>
  );
};

export default Navigation;
